// components/RegistrationPaymentBanner.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../providers/AuthProvider';
import { useToast } from '../providers/ToastProvider';
import { AlertCircle, ArrowRight, Loader2, X } from 'lucide-react';

/**
 * Persistent banner shown to unpaid agents / sub-agents.
 * Mount it once inside the authenticated layout (below the header).
 */
const RegistrationPaymentBanner = () => {
    const { colors } = useTheme();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const {
        isUnpaidAgent,
        initiateRegistrationPayment,
        pollRegistrationStatus,
        refreshUser,
        clearUnpaidGate,
        user,
    } = useAuth();

    const [busy, setBusy] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    if (!isUnpaidAgent || dismissed) return null;

    const handleComplete = async () => {
        setBusy(true);
        try {
            const res = await initiateRegistrationPayment();

            if (!res.ok) {
                showToast(res.error || 'Could not start payment.', 'error');
                setBusy(false);
                return;
            }

            const d = res.data || {};

            // Case: fee zero — account auto-activated
            if (d.zero_fee) {
                await refreshUser();
                clearUnpaidGate();
                showToast('Registration completed. Welcome!', 'success');
                navigate('/');
                return;
            }

            // Case: already paid via Paystack, webhook pending
            if (d.already_paid) {
                showToast('Payment confirmed. Finishing setup…', 'info');
                const poll = await pollRegistrationStatus(user?.id);
                if (poll.ok) {
                    clearUnpaidGate();
                    showToast('Registration complete.', 'success');
                    navigate('/');
                } else {
                    showToast(
                        poll.error || 'Still confirming. Please check back shortly.',
                        'warning'
                    );
                }
                setBusy(false);
                return;
            }

            // Case: normal — redirect to Paystack
            if (d.authorization_url) {
                if (d.callback_url) {
                    localStorage.setItem('registration_dashboard_url', d.callback_url);
                }
                window.location.href = d.authorization_url;
                return;
            }

            showToast('Unexpected response. Please try again.', 'error');
            setBusy(false);
        } catch (e) {
            showToast('Network error. Please try again.', 'error');
            setBusy(false);
        }
    };

    return (
        <div
            style={{
                background: colors.warningBg || '#FEF3C7',
                borderBottom: `1px solid ${colors.warning || '#F59E0B'}`,
                padding: '10px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
            }}
        >
            <AlertCircle
                size={18}
                style={{ color: colors.warning || '#F59E0B', flexShrink: 0 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div
                    style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: colors.heading,
                    }}
                >
                    Registration payment required
                </div>
                <div
                    style={{
                        fontSize: 12,
                        color: colors.secondaryText,
                        marginTop: 2,
                    }}
                >
                    Complete your one-time fee to unlock write actions.
                </div>
            </div>
            <button
                type="button"
                onClick={handleComplete}
                disabled={busy}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 14px',
                    borderRadius: 8,
                    border: 'none',
                    background: colors.warning || '#F59E0B',
                    color: '#fff',
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: busy ? 'not-allowed' : 'pointer',
                    opacity: busy ? 0.7 : 1,
                    fontFamily: 'inherit',
                    flexShrink: 0,
                }}
            >
                {busy ? (
                    <>
                        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                        Processing…
                    </>
                ) : (
                    <>
                        Complete payment
                        <ArrowRight size={14} />
                    </>
                )}
            </button>
            <button
                type="button"
                onClick={() => setDismissed(true)}
                aria-label="Dismiss"
                style={{
                    background: 'transparent',
                    border: 'none',
                    color: colors.secondaryText,
                    cursor: 'pointer',
                    padding: 4,
                    flexShrink: 0,
                }}
            >
                <X size={16} />
            </button>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default RegistrationPaymentBanner;